import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n, ready } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

  // Update currentLanguage when i18next is ready and language is detected
  useEffect(() => {
    if (ready && i18n.language) {
      setCurrentLanguage(i18n.language);
    }
  }, [ready, i18n.language]);

  // Available languages
  const supportedLanguages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  // Update state when i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Change language function
  const changeLanguage = async (languageCode) => {
    try {
      await i18n.changeLanguage(languageCode);
      setCurrentLanguage(languageCode);

      // Store in localStorage for persistence
      localStorage.setItem('i18nextLng', languageCode);

      // Trigger a re-render to update any components that might not be automatically updating
      window.dispatchEvent(new Event('languageChanged'));

      return true;
    } catch (error) {
      console.error('Failed to change language:', error);
      return false;
    }
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return supportedLanguages.find(lang => lang.code === currentLanguage) || supportedLanguages[0];
  };

  // Check if language is supported
  const isLanguageSupported = (languageCode) => {
    return supportedLanguages.some(lang => lang.code === languageCode);
  };

  // Get language name by code
  const getLanguageName = (languageCode) => {
    const lang = supportedLanguages.find(l => l.code === languageCode);
    return lang ? lang.name : languageCode;
  };

  // Get language flag by code
  const getLanguageFlag = (languageCode) => {
    const lang = supportedLanguages.find(l => l.code === languageCode);
    return lang ? lang.flag : '🌐';
  };

  const value = {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    getCurrentLanguageInfo,
    isLanguageSupported,
    getLanguageName,
    getLanguageFlag,
    isLoading: false // Can be used for loading states during language switches
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;